/**
 * O registro dos módulos — o que existe no banco e o que existe na tela.
 *
 * ── Por que este arquivo existe ────────────────────────────────────────
 *
 * Em 20 e 21/08/2026 as migrações `0021` a `0028` entregaram aferição
 * interna, compactação, ISC, expansão e adensamento: regra, cálculo,
 * trava e vocabulário, tudo no banco e tudo testado. Nenhum deles tinha
 * tela. A Testesolo tentou acessar os "módulos novos" no `labmanager` e
 * não achou nada — porque não havia nada para achar, e eu tinha
 * anunciado como se houvesse.
 *
 * O defeito não foi a falta da tela: foi o **descompasso invisível**. Um
 * módulo pode legitimamente ficar semanas só no banco; o que não pode é
 * ninguém conseguir ver que é esse o estado dele.
 *
 * Então o estado da interface vira dado declarado, e não memória de quem
 * escreveu:
 *
 *  · o **menu lateral é gerado daqui** — não há uma segunda lista escrita
 *    à mão que possa discordar desta;
 *  · o teste `18_modulos` reprova se uma tabela de domínio no banco não
 *    estiver reivindicada por nenhum módulo. Migração nova sem declarar o
 *    estado da interface derruba a suíte;
 *  · o mesmo teste reprova módulo `no_ar` sem rota e sem arquivo de tela,
 *    e módulo sem rota que apareça como link.
 *
 * Resultado prático: da próxima vez que um módulo subir só no banco, a
 * suíte fica vermelha antes de a Testesolo descobrir na tela — e o item
 * aparece no menu apagado, com a etiqueta do que falta.
 */

export type Situacao = 'no_ar' | 'em_breve' | 'fase_2';

export interface Modulo {
  /** Chave curta, estável. Usada em teste e em log, nunca na tela. */
  chave: string;
  /** O que aparece no menu. */
  titulo: string;
  /** Agrupamento do menu; `null` é o bloco de cima, sem título. */
  grupo: string | null;
  /** Símbolo do menu — decorativo, `aria-hidden`. */
  ic: string;
  /**
   * Tabelas de domínio que este módulo governa. É por esta lista que o
   * teste 18 sabe que uma migração nova não ficou órfã de interface.
   */
  tabelas: string[];
  /** Rota da tela, quando existe. `null` = ainda não há tela. */
  rota: string | null;
  /** Prefixo que marca o item como corrente (telas de detalhe). */
  raiz?: string;
  /**
   * Prefixos de permissão que este módulo governa — `equipamento` cobre
   * `equipamento.ver`, `equipamento.editar` e as demais.
   *
   * Governa o ALCANCE (`alcanca`): o item do menu, o cartão da porta e o
   * destino do clique num espaço só valem para quem tem alguma permissão de
   * um destes prefixos. Desde a H1 (onda 295, spec 03 §9) todo módulo de
   * menu declara o seu, até o apagado da fase 2 — módulo sem `permissoes`
   * aparecia para todos, e o menu de um espaço alcançado mostrava o que a
   * pessoa não alcança. A grade de cadeados da tela de usuários não lê mais
   * este campo: ela vem do catálogo `acesso` (0205).
   */
  permissoes?: string[];
  /**
   * Onde o módulo aparece na moldura. Ausente = `'menu'`.
   *
   * ── Por que este campo nasceu (23/08/2026) ──────────────────────────
   *
   * O registro tinha uma equação implícita: *módulo = item de menu*.
   * Enquanto havia catorze módulos ela funcionou. Com a lista de métodos
   * que a Testesolo mandou — triviais, especiais, triaxiais estáticos e
   * dinâmicos, mistura asfáltica — o menu ia a 36 itens, e continuaria
   * crescendo a cada método novo. O menu deixaria de ser mapa e viraria
   * índice.
   *
   * A opção B, escolhida em 23/08, quebra a equação: um módulo continua
   * declarando as tabelas de que responde (é disso que o teste 18 vive),
   * mas onde ele aparece passa a ser escolha:
   *
   *  · `menu` — o trabalho do dia. O que produz registro técnico.
   *  · `engrenagem` — administração do sistema, ao lado do nome de quem
   *    está logado. Coisa que se mexe uma vez por mês, não por turno.
   *  · `oculto` — existe no banco e tem dono declarado, mas não se chega
   *    a ele por menu: chega-se por dentro de outra tela. É o caso de
   *    cada método de ensaio — o técnico parte da amostra ou da linha da
   *    fila, e o sistema já sabe qual método é, porque está na
   *    programação da OS. Um item de menu seria um SEGUNDO caminho para
   *    um destino ao qual ele já chega, e o segundo caminho é o que fica
   *    desatualizado.
   */
  local?: Local;
  /**
   * A chave do módulo que serve de PORTA para este (08/09/2026).
   *
   * «O cadastro de itens está ocupando muito do menu lateral. Acredito
   *  que deva ter um local centralizado para cadastrar coisas, como
   *  cliente, fornecedores de calibração, locais de armazenamento»
   * (Humberto, 08/09/2026).
   *
   * Um módulo `oculto` com `dentroDe` continua sem item de menu, mas não
   * está perdido: a tela do módulo-porta o lista como cartão, e o item
   * da porta acende no menu enquanto se está nele. É a terceira maneira
   * de um módulo ser alcançado — depois do menu e da engrenagem — e a
   * única em que o menu NÃO cresce quando o catálogo de cadastros
   * cresce.
   */
  dentroDe?: string;
  /**
   * Uma frase para o cartão da porta: o que se cadastra aqui e para
   * quem. Só faz sentido com `dentroDe`.
   */
  resumo?: string;
  /**
   * Em que espaço(s) de trabalho o módulo mora (onda 293, 07/10/2026).
   *
   * ── Por que este campo nasceu ───────────────────────────────────────
   *
   * A especificação 03 (Hub · LabManager · Campo, §8) divide o sistema
   * em três espaços de trabalho, e a faixa de cima passa a mostrar
   * «Hub · LabManager · Campo» no lugar das áreas de hoje (Trabalho,
   * Bancada, Saída, SGQ), que viram grupos DENTRO dos espaços. O menu
   * lateral de cada espaço mostra só o que mora nele — e quem diz o que
   * mora onde é este campo, no mesmo registro de sempre, e não uma
   * segunda tabela escrita à mão que um dia discordaria do menu (a mesma
   * razão do `local` e do `dentroDe`).
   *
   * `'sistema'` não é espaço de trabalho: é a engrenagem, nunca aparece
   * na faixa, e quem mora nele mora só nele. Por isso `Espaco` é
   * `EspacoDeTrabalho | 'sistema'`, e a lista `ESPACOS` — a da faixa —
   * não o tem.
   *
   * ── Por que três módulos moram em mais de um espaço ─────────────────
   *
   *  · `os` — Hub e LabManager. A OS integrada (o serviço inteiro, das
   *    duas metades) e a OS de laboratório (a `-L`) só se separam na fase
   *    H2, quando nasce a OS integrada; até lá é a MESMA tela e o mesmo
   *    registro, vistos dos dois lados.
   *  · `equipamentos` e `padroes` — Hub, LabManager e Campo. O parque é
   *    um só, e a spec manda os três espaços o mostrarem filtrado pelo
   *    âmbito da categoria (o que é de laboratório, o que é de campo, o
   *    que é dos dois). Esse filtro é da fase H1; até lá os três espaços
   *    abrem a lista inteira.
   *
   * Clicar num módulo compartilhado NÃO troca de espaço: quem está no
   * Campo e abre «Equipamentos e calibrações» continua no Campo. A
   * escolha do espaço é da pessoa, e o módulo só diz onde pode ser visto.
   *
   * (O Painel mora só no LabManager. Desde a onda 550 o Hub abre no
   * Início, «O que precisa de você hoje», e não mais na lista de OS.)
   */
  espacos: Espaco[];
  situacao: Situacao;
}

/** Onde o módulo aparece na moldura. */
export type Local = 'menu' | 'engrenagem' | 'oculto';

/**
 * Os espaços de trabalho — o que a faixa de cima oferece (spec 03, §3).
 *
 * `comercial` (0500, onda 347) nasceu fechado no banco
 * (`espaco.de_trabalho = false`) e abriu na 0507 (onda 550, Tarefa 14 do
 * Plano 2), junto com a tela da caixa de entrada: está em `ESPACOS`, depois
 * do Campo.
 */
export type EspacoDeTrabalho = 'hub' | 'labmanager' | 'campo' | 'comercial';

/**
 * Onde um módulo mora: um espaço de trabalho ou o Sistema, que é a
 * engrenagem e nunca aparece na faixa.
 */
export type Espaco = EspacoDeTrabalho | 'sistema';

export const ETIQUETA: Record<Situacao, string | null> = {
  no_ar: null,
  em_breve: 'em breve',
  fase_2: 'fase 2',
};

export const MODULOS: Modulo[] = [
  {
    /*
     * A porta de entrada, desde 23/08/2026.
     *
     * A rota é `/` e não `/painel`: quem digita o endereço do sistema
     * chega aqui. Até esta data `/` caía no Recebimento — herança de
     * quando ele era a única tela que existia —, e a Testesolo apontou
     * que estava errado: a pergunta de quem abre o sistema pela manhã é
     * "o que precisa de mim hoje", e o recebimento é uma das respostas
     * possíveis, não a pergunta.
     *
     * `raiz` faz `/painel` continuar valendo, para quem tem o endereço
     * salvo não receber um 404 por causa de uma mudança de arrumação.
     */
    chave: 'painel', titulo: 'Painel', grupo: null, ic: '▤',
    espacos: ['labmanager'],
    tabelas: [], permissoes: ['indicador'],
    rota: '/', raiz: '/painel', situacao: 'no_ar',
  },
  {
    /*
     * 550 (Tarefa 20 do Plano 2) — o Início do Hub, «O que precisa de você
     * hoje» (spec do Comercial §4.5): os pendentes de todos os espaços que a
     * pessoa alcança, com o que trava primeiro — do Comercial, o Radar dela,
     * as conversas passadas a ela e as tarefas de hoje; do LabManager, os
     * ensaios esperando a verificação ou a aprovação dela. É a porta do Hub
     * (`PREFERENCIA_DO_ESPACO.hub`). Sem tabela: lê as dos espaços
     * (`GET /hub/hoje`).
     *
     * `permissoes`: o da OS, que é por onde todo tipo do Hub entrava até
     * aqui, e os do Comercial (`atendimento`, `crm`). Não o da bancada
     * (`ensaio`): ele é do LabManager, e daria ao Hub um destino para quem o
     * alcança só por uma permissão sem tela (282.33, 282.34). Nem o do Mapa:
     * quem só tem o «Ver» do Mapa tem, no Hub, só o Mapa (282.31) — e nenhuma
     * fonte teria o que lhe mostrar.
     */
    chave: 'inicio', titulo: 'Início', grupo: null, ic: '⌂',
    espacos: ['hub'],
    tabelas: [], permissoes: ['os', 'atendimento', 'crm'],
    rota: '/inicio', situacao: 'no_ar',
  },

  // ── Trabalho ──────────────────────────────────────────────────────
  // O que entra e o que sai do laboratório. É por aqui que o serviço
  // chega: cliente manda a OS, o material chega no recebimento, vira
  // amostra, entra na programação.
  {
    chave: 'os', titulo: 'Ordens de Serviço', grupo: 'Trabalho', ic: '▣',
    espacos: ['hub', 'labmanager'],
    /*
     * A OS é o núcleo do briefing (item 5) e foi o último dos módulos
     * centrais a ganhar tela: existia no banco desde a `0001`, era criada
     * por script de carga, e o recebimento aceitava lote órfão justamente
     * para não depender dela. O escopo contratado continua só de leitura
     * — depende do catálogo de `item_medicao`, que ainda não tem cadastro.
     */
    tabelas: ['ordem_servico', 'os_ensaio_previsto', 'os_item_escopo',
      'os_status_historico', 'os_amostra_prevista',
      'os_revisao'],
    permissoes: ['os'], rota: '/os', raiz: '/os', situacao: 'no_ar',
  },
  {
    chave: 'recebimento', titulo: 'Recebimento', grupo: 'Trabalho', ic: '▼',
    espacos: ['labmanager'],
    tabelas: ['lote_recebimento', 'lote_sequencia'],
    permissoes: ['recebimento'],
    rota: '/recebimentos', raiz: '/recebimento', situacao: 'no_ar',
  },
  {
    chave: 'amostras', titulo: 'Amostras', grupo: 'Trabalho', ic: '◇',
    espacos: ['labmanager'],
    /*
     * A lista existe desde a 0037. Até então só se chegava a uma amostra
     * pelo QR ou pelo recebimento que a criou — os dois pressupõem que
     * você já sabe qual quer. A pergunta inversa (*quais estão
     * esperando ensaio?*) não tinha resposta na tela, e é a que o
     * laboratório faz o dia inteiro.
     */
    tabelas: ['amostra', 'amostra_movimentacao', 'amostra_sequencia'],
    permissoes: ['amostra'],
    rota: '/amostras', raiz: '/amostra', situacao: 'no_ar',
  },
  {
    /*
     * Estava em "Controle" como item próprio até 23/08/2026. Mudou de
     * lugar a pedido da Testesolo, e a mudança está certa: descarte não
     * é um lugar aonde se vai, é o que se faz com uma amostra que já
     * está na frente da pessoa. Vira uma VISTA de Amostras — o filtro
     * "prontas para descarte" e a ação em lote —, e não um segundo
     * caminho que precisaria repetir a busca de amostra inteira.
     */
    chave: 'descarte', titulo: 'Descarte de amostras', grupo: 'Trabalho',
    ic: '⌫',
    espacos: ['labmanager'],
    /*
     * ── Onda 174 (0175): a fila ganhou tela — e continua fora do menu ──
     *
     * A decisão de 23/08 fica de pé: descartar UMA amostra é gesto da
     * ficha dela, e lá ficou. A tela nova (`/descarte`) responde outra
     * pergunta — «o que pode sair da estante hoje, e por que o resto
     * não pode?» — e chega-se a ela por dentro de Amostras (o botão
     * «Fila de descarte») e pelo Painel («amostras com prazo vencido»).
     * Não vai ao menu para não gastar o teto do 33.1 com um segundo
     * caminho para o mesmo acervo. As duas tabelas são os registros que
     * rodeiam a baixa: a retenção a pedido do cliente (RN-184) e a
     * autorização de descarte antecipado (gestor/RT).
     */
    tabelas: ['amostra_retencao', 'amostra_descarte_autorizacao'],
    permissoes: ['arquivo'], rota: '/descarte', raiz: '/descarte',
    local: 'oculto', situacao: 'no_ar',
  },
  {
    /*
     * Subiu de "Controle" para "Trabalho" em 23/08/2026. Cronograma é o
     * que a Testesolo olha para saber o que a equipe faz amanhã — a
     * mesma pergunta da OS e do recebimento. Ao lado de calibração e
     * normas ele parecia rotina administrativa, que é o oposto do que é.
     */
    chave: 'cronograma', titulo: 'Cronograma', grupo: 'Trabalho', ic: '▬',
    espacos: ['labmanager'],
    /*
     * A LEITURA do cronograma não tem tabela própria — o ensaio é do
     * módulo `ensaios`, o dia de campo e o furo são do módulo `campo`, e
     * o registro exige um dono só por tabela.
     *
     * `ensaio_programacao` (0152) é a exceção, e é dona daqui: ela não
     * é dado de ensaio nem de campo. É a ALOCAÇÃO — em que máquina o
     * ensaio vai correr e por quantos dias — e quem responde por ela é
     * quem programa, que é esta tela.
     */
    tabelas: ['ensaio_programacao', 'ensaio_programacao_base',
      'metodo_dependencia', 'ensaio_programacao_revisao',
      /* 0191/0195: a exceção por máquina («a MS102 também faz adensamento»)
         é planejamento — quem a declara é quem programa. */
      'equipamento_grupo_extra', 'frente_servico', 'frente_metodo'],
    permissoes: ['ensaio'],
    rota: '/cronograma', situacao: 'no_ar',
    /*
     * Onda 200: `/cronograma` É a linha do tempo por equipamento (0152).
     * A tela por atividade que morava aqui saiu — o menu abria a que
     * ninguém usava. `/cronograma/maquinas` só redireciona.
     */
    raiz: '/cronograma',
  },

  // ── Bancada ───────────────────────────────────────────────────────
  // Dois itens, e continuam dois com sessenta métodos no catálogo. O
  // método não é item de menu: é linha da fila (ver `local`).
  {
    chave: 'fila', titulo: 'Fila do laboratório', grupo: 'Bancada', ic: '≡',
    espacos: ['labmanager'],
    /*
     * A tabela `ensaio` existe desde a 0001 e até a 0037 não tinha
     * visão nem rota: a fila só existia para quem abre o banco. A tela é
     * somente leitura — lançar dado depende dos cálculos validados
     * (PD-68 a PD-72).
     */
    /*
     * `tabelas` fica vazio de propósito: a dona de `ensaio` é o módulo
     * `ensaios`, e o registro exige um dono só por tabela — é o que
     * impede duas telas divergirem sobre quem responde por um dado. A
     * fila é uma LEITURA dessa tabela, não a casa dela.
     */
    tabelas: [],
    permissoes: ['ensaio'],
    rota: '/fila', situacao: 'no_ar',
  },
  {
    /*
     * Verificação e aprovação — a pergunta ao contrário da fila.
     *
     * «Não encontrei o caminho para aprovar um ensaio. Talvez seja
     *  melhor criar um item separado, à parte da fila do laboratório»
     * (Testesolo, 02/09/2026).
     *
     * São dois públicos. A Fila é de quem LANÇA: mostra o que falta
     * fazer. Esta é de quem CONFERE e ASSINA: mostra o que está pronto e
     * parado esperando um nome. Misturadas, a etapa de aprovação ficava
     * invisível — quem aprova tinha de varrer a fila procurando.
     *
     * `tabelas` vazio pela mesma razão da Fila: a dona de `ensaio` é o
     * módulo `ensaios`, e esta tela é uma LEITURA dela.
     *
     * Declara `ensaio` desde a H1 (onda 295): o menu de um espaço alcançado
     * só mostra o que a pessoa alcança, e módulo sem `permissoes` aparecia
     * para todos — inclusive para quem não tem nada da bancada. A coluna da
     * matriz de acessos não depende mais deste campo (ela vem do catálogo
     * `acesso`, 0205), então declarar o prefixo não a alarga: os cadeados de
     * `ensaio.verificar` e `ensaio.aprovar` continuam na unidade Ensaios.
     */
    chave: 'assinatura', titulo: 'Verificação e aprovação',
    grupo: 'Bancada', ic: '✓',
    espacos: ['labmanager'],
    tabelas: [],
    permissoes: ['ensaio'],
    rota: '/assinatura', raiz: '/assinatura', situacao: 'no_ar',
  },
  {
    /*
     * A Estufa — a pesagem seca de todas as cápsulas (onda 290, RN-235).
     *
     * «A massa da cápsula+solo seco é feita 24h depois» (Humberto,
     * 06/10/2026). De manhã a bancada pesa as cápsulas de vários ensaios
     * na ordem em que saem da estufa; o que se tem na mão é o número da
     * cápsula. É a terceira porta da bancada, e não um método: as linhas
     * moram nas fichas de umidade, LL e LP (por isso `tabelas` vazio), e a
     * ficha continua aceitando a mesma pesagem pela mesma rota.
     */
    chave: 'estufa', titulo: 'Estufa', grupo: 'Bancada', ic: '♨',
    espacos: ['labmanager'],
    tabelas: [],
    permissoes: ['ensaio'],
    rota: '/estufa', situacao: 'no_ar',
  },
  {
    /*
     * Programar é o que ENCHE a fila, e faltava desde sempre.
     *
     * A `ensaio` existe desde a `0001`; a fila lê dela desde a `0037`. O
     * que não existia era o caminho de escrita: até 23/08/2026 um ensaio
     * só nascia por INSERT no banco, e a fila mostrava vazio para sempre
     * numa tela que funcionava.
     *
     * Este módulo é o tronco comum dos métodos — a `ensaio` e o que a
     * cerca. Nenhuma tela de método é dele sozinho, mas as tabelas
     * precisam de dono para o teste `18` não apontá-las órfãs.
     */
    chave: 'ensaios', titulo: 'Programar ensaios', grupo: 'Bancada', ic: '＋',
    espacos: ['labmanager'],
    tabelas: ['ensaio', 'ensaio_dado_bruto', 'ensaio_estagio', 'ensaio_resultado',
      'ensaio_equipamento', 'ensaio_norma_versao', 'metodo_ensaio',
      /* A lista do que falta e a justificativa de cada uma que fica
         (0089): a pendência é do ensaio, e é na ficha dele que ela
         aparece e é respondida. */
      'ficha_pendencia', 'ensaio_pendencia_justificada',
      /* Como uma tabela de dado sem `ensaio_id` chega ao ensaio (0118).
         É catálogo do avanço automático, e o avanço é do ensaio. */
      'ensaio_ligacao',
      'classe_metodo', 'ensaio_evidencia', 'ensaio_status_historico',
      'tabela_do_ensaio_isenta', 'bateria_ensaio', 'bateria_ensaio_metodo'],
    permissoes: ['ensaio'],
    /*
     * SAIU DO MENU em 30/08/2026, e a razão é do Humberto:
     *
     * «a programação de ensaios para amostras deve ocorrer em apenas uma
     *  tela, e deve ser na OS. (…) Do jeito que está está confuso. Pode
     *  confundir quem for fazer a programação.»
     *
     * Um item de menu chamado "Programar ensaios" é uma segunda porta
     * para a mesma coisa — e a porta do menu chega sem OS, obrigando a
     * pessoa a escolher uma antes de fazer o que veio fazer. Programar é
     * um ato DA ORDEM DE SERVIÇO; o caminho é abrir a OS.
     *
     * A pergunta «quais OS ainda precisam ser programadas?» não se perde:
     * a lista de OS tem o filtro «Aguardando programação» desde a 0059.
     */
    rota: '/os/:id/programar',
    /*
     * `raiz` fica no endereço ANTIGO porque é dele que o módulo responde:
     * `/programar` continua existindo como redirecionamento para a lista
     * de OS, e rota sem módulo dono é o que o teste 18.4 reprova. Quem
     * herdou o endereço herda a responsabilidade por ele.
     */
    raiz: '/programar', local: 'oculto', situacao: 'no_ar',
  },

  // ── Os métodos, fora do menu ──────────────────────────────────────
  //
  // Continuam sendo módulos: declaram as tabelas de que respondem, e é
  // por isso que o teste 18 não os aponta órfãos. O que eles não são é
  // item de menu — chega-se a cada um pela amostra ou pela linha da
  // fila, que já sabem qual método é. Ver `local` no tipo `Modulo`.
  {
    chave: 'limites', titulo: 'Limites de Atterberg', grupo: 'Bancada', ic: '◔',
    espacos: ['labmanager'],
    /*
     * LL e LP são normas NBR separadas e são executados em momentos
     * diferentes — por isso entram na fila um de cada vez. O RELATÓRIO
     * sai junto, porque o IP depende dos dois (Testesolo, 23/08/2026).
     * Um módulo só, portanto, e não dois: o que se emite é um documento.
     */
    tabelas: [], rota: null, local: 'oculto', situacao: 'em_breve',
  },
  {
    /*
     * Granulometria por peneiramento — ABNT NBR 7181:2025. A sedimentação
     * NÃO está aqui: `ensaio_granulometria.com_sedimentacao` está travado
     * em false pela 0048, porque as calibrações por densímetro ainda não
     * existem (PD-93). O módulo entra `no_ar` mesmo assim porque o
     * peneiramento é ensaio inteiro, do lançamento ao PDF, e não a metade
     * de nada.
     *
     * `peneira_serie` mora aqui por ser catálogo de uma coisa só: a série
     * de peneiras da NBR 7181 e as intermediárias que a Testesolo usa.
     */
    /*
     * A passagem de mão do ensaio: o §12 inteiro, numa tela.
     *
     * Fica OCULTA do menu porque não é um lugar aonde se vai — é aonde se
     * chega, pela linha do ensaio na fila. Um item de menu "situação do
     * ensaio" perguntaria «qual ensaio?» antes de servir para alguma
     * coisa, e a resposta a essa pergunta é justamente a fila.
     *
     * `tabelas` fica vazio: a dona de `ensaio` e do histórico é o módulo
     * `ensaios`. Esta tela é uma LEITURA e um caminho de escrita sobre a
     * casa alheia — e o registro exige um dono só por tabela, que é o que
     * impede duas telas divergirem sobre quem responde por um dado.
     */
    chave: 'ensaio-situacao', titulo: 'Situação do ensaio',
    grupo: 'Bancada', ic: '⇄',
    espacos: ['labmanager'],
    tabelas: [],
    permissoes: ['ensaio'],
    rota: '/ensaio/:id/situacao', local: 'oculto', situacao: 'no_ar',
  },
  {
    chave: 'granulometria', titulo: 'Granulometria', grupo: 'Bancada', ic: '◫',
    espacos: ['labmanager'],
    tabelas: ['ensaio_granulometria', 'granulometria_umidade',
      'granulometria_peneira', 'peneira_serie',
      /* A sedimentação (0221): as leituras do densímetro e a Tabela 2 da
         NBR 7181:2025, que é transcrição de norma e só muda por migração. */
      'granulometria_sedimentacao', 'agua_viscosidade_nbr7181'],
    rota: '/ensaio/:id/granulometria', local: 'oculto', situacao: 'no_ar',
  },
  {
    /*
     * Era "Compactação e ISC", um módulo só, enquanto os dois estavam
     * apenas no banco. Em 23/08/2026 a compactação ganhou tela e o ISC
     * não — e um módulo que é metade `no_ar` e metade `em_breve` não
     * consegue dizer a verdade sobre nenhuma das duas metades.
     *
     * `compactacao_energia` fica AQUI, com o ISC, porque a Tabela 1 da
     * NBR 7182 governa os dois: o corpo de prova do ISC é moldado com a
     * mesma energia. Poderia ir para qualquer um dos lados; o que não
     * pode é ir para os dois.
     */
    chave: 'massa_especifica', titulo: 'Massa esp. dos sólidos',
    grupo: 'Bancada', ic: '◍',
    espacos: ['labmanager'],
    /*
     * `agua_massa_especifica` é a Tabela A.1 do Anexo A da NBR 17212:2025
     * — catálogo normativo, não dado de ensaio. Mora aqui porque é aqui
     * que ela entrou; quando a sedimentação chegar, as duas a lerão sem
     * que ela precise mudar de dono.
     *
     * `picnometro_calibracao` é a curva M1 = f(T) do picnômetro. Poderia
     * argumentar-se que pertence a Equipamentos — mas é a NBR 17212 que
     * a define, e é só este ensaio que a lê.
     */
    tabelas: ['ensaio_massa_especifica_solidos', 'mes_umidade',
      'mes_determinacao', 'picnometro_calibracao', 'agua_massa_especifica'],
    rota: '/ensaio/:id/massa-especifica', local: 'oculto', situacao: 'no_ar',
  },
  {
    /*
     * A classificação SUCS e HRB (onda 217, RN-205): um ensaio próprio da
     * OS, medido e cobrado à parte, que lê a granulometria e os limites
     * aprovados da mesma amostra. A tabela guarda só a escolha das
     * fontes; a conta é a `vw_ensaio_classificacao`.
     */
    chave: 'classificacao', titulo: 'Classificação SUCS e HRB',
    grupo: 'Bancada', ic: '◈',
    espacos: ['labmanager'],
    tabelas: ['ensaio_classificacao'],
    rota: '/ensaio/:id/classificacao', local: 'oculto', situacao: 'no_ar',
  },
  {
    chave: 'isc', titulo: 'ISC e expansão', grupo: 'Bancada', ic: '◉',
    espacos: ['labmanager'],
    tabelas: ['compactacao_energia',
      'ensaio_isc', 'isc_corpo_prova', 'isc_penetracao_leitura',
      'isc_expansao_leitura', 'isc_penetracao_norma', 'isc_pressao_padrao'],
    rota: '/ensaio/:id/isc', local: 'oculto', situacao: 'no_ar',
  },
  {
    /*
     * No ar desde 23/08/2026 — a tela de lançamento vai do anel ao PDF.
     *
     * A rota é por ensaio (`/ensaio/:id/adensamento`) e não uma listagem
     * própria: a lista de adensamentos JÁ existe, é a Fila do laboratório
     * filtrada por método. Um segundo lugar para ver a mesma coisa é um
     * segundo lugar para ela ficar desatualizada.
     *
     * Por isso `local: 'oculto'`: não há item de menu, há o caminho a
     * partir da fila. O teste `18` aceita módulo no ar sem item de menu;
     * o que ele não aceita é módulo que apareça no menu sem rota.
     *
     * Desde 23/08/2026 são DUAS telas — `/ensaio/:id/adensamento` para a
     * bancada e `.../interpretacao` para quem elabora o relatório —, e um
     * módulo só. A divisão é de ofício e de permissão (`ensaio.lancar` ×
     * `ensaio.processar`), não de assunto: as duas telas leem o mesmo
     * ensaio e escrevem nas mesmas tabelas. Dois módulos aqui
     * espalhariam o mesmo ensaio por dois lugares do menu.
     *
     * `prensa_adensamento` é catálogo, não movimento: guarda o formato do
     * arquivo bruto de cada família de prensa e a convenção de sinal que
     * ele adota (`0055`). Fica neste módulo porque é o adensamento quem
     * a consome; no dia em que o cisalhamento direto ler o mesmo arquivo
     * da MS102, ela sobe para os equipamentos.
     */
    chave: 'adensamento', titulo: 'Adensamento', grupo: 'Bancada', ic: '◑',
    espacos: ['labmanager'],
    tabelas: ['ensaio_adensamento', 'adensamento_estagio', 'adensamento_leitura',
      'adensamento_cv', 'adensamento_compressao', 'adensamento_umidade',
      'adensamento_arquivo', 'adensamento_pre_adensamento',
      'adensamento_tempo_norma', 'prensa_adensamento',
      /*
       * A conferência nominal × média lida (`0058`) e as duas tolerâncias
       * que a Testesolo ajusta. Ficam aqui porque quem as usa é a tela do
       * adensamento: a tabela de conferência é do bloco 4, e a pendência
       * que elas geram trava o laudo deste ensaio.
       */
      'adensamento_tensao_lida', 'parametro_adensamento'],
    /*
     * `raiz` cobre a segunda tela. Não é atalho para calar o teste `18.4`:
     * é a afirmação de que este módulo governa a ÁRVORE
     * `/ensaio/:id/adensamento/…`, e que qualquer tela que nasça dentro
     * dela é adensamento — não um módulo novo que alguém esqueceu de
     * declarar.
     */
    rota: '/ensaio/:id/adensamento', raiz: '/ensaio/:id/adensamento',
    local: 'oculto', situacao: 'no_ar',
  },

  {
    /*
     * Triaxial — CIU, CID e UU, do bruto da prensa ao laudo.
     *
     * A onda 137 levantou a estrutura (`0147`), a 142 trouxe as três
     * etapas do arquivo (`0150`) e o módulo ficou `em_breve`, SEM ROTA
     * DECLARADA — sem tela, e dizendo isso, porque o critério de ruptura
     * ainda era pergunta em aberto e o §25 proíbe implementar conta de
     * ensaio sem resposta.
     *
     * (A frase acima não escreve o par «campo: valor» de propósito: o
     * teste `18` extrai o registro por expressão regular, e um exemplo
     * escrito no comentário casa antes do campo de verdade. Foi o que
     * aconteceu na primeira versão desta entrada — o módulo aparecia
     * como se ainda não tivesse tela.)
     *
     * Em 08/09/2026 a resposta veio: o critério é declarado CORPO A
     * CORPO, como as abas da Suporte fazem, e a envoltória se ajusta no
     * diagrama p'–q'. Com isso a `0170` fechou o método — índices
     * físicos, ponto de ruptura, envoltória — e a tela nasceu dividida
     * nas mesmas fases do ensaio.
     *
     * `triaxial_umidade` entra aqui e não num módulo de umidade: as
     * cápsulas são DO CORPO DE PROVA do triaxial, e a média delas é o w₀
     * que vira e₀ na folha deste laudo. Num módulo à parte, uma correção
     * na cápsula chegaria a este ensaio por caminho que ninguém declarou.
     */
    chave: 'triaxial', titulo: 'Triaxial', grupo: 'Bancada', ic: '◒',
    espacos: ['labmanager'],
    tabelas: ['ensaio_triaxial', 'triaxial_cp', 'triaxial_saturacao_estagio',
      'triaxial_adensamento_leitura', 'triaxial_cisalhamento_leitura',
      'triaxial_umidade'],
    rota: '/ensaio/:id/triaxial',
    local: 'oculto', situacao: 'no_ar',
  },

  {
    /*
     * Cisalhamento direto — CD.IN, CD.NAT e CD.GD (onda 265, RN-225).
     *
     * A ficha no molde da do triaxial, pedida assim pelo Humberto em
     * 03/10/2026: amostra, adensamento, cisalhamento, a interpretação —
     * onde moram os gráficos — e o laudo. As contas (tensão nominal e
     * corrigida, os quatro critérios, a envoltória, t₉₀, t₁₀₀ e as
     * velocidades) são da `0236`, conferidas contra a planilha
     * 2606282 CA-04 da Testesolo.
     *
     * `oculto` pela mesma razão do triaxial: a lista destes ensaios é a
     * Fila do laboratório filtrada por método.
     */
    chave: 'cisalhamento-direto', titulo: 'Cisalhamento direto', grupo: 'Bancada', ic: '◫',
    espacos: ['labmanager'],
    tabelas: ['ensaio_cisalhamento_direto', 'cisalhamento_direto_cp',
      'cisalhamento_direto_umidade', 'cisalhamento_direto_adensamento_leitura',
      'cisalhamento_direto_leitura', 'cisalhamento_direto_tracado',
      'cisalhamento_direto_arquivo'],
    rota: '/ensaio/:id/cisalhamento-direto',
    local: 'oculto', situacao: 'no_ar',
  },

  {
    /*
     * Módulo de resiliência, DNIT 181/2018-ME — a ficha do material
     * estabilizado quimicamente.
     *
     * No ar desde 02/09/2026 — da importação do arquivo da prensa ao
     * PDF. As contas, o item 8 (faixa de 25 a 50 µm), o item 9
     * (triplicata a ±10 %) e o modelo K₁·σd^K₂ foram conferidos contra a
     * planilha do relatório 2607315 da Testesolo, célula a célula, antes
     * de virarem migração — os seis corpos de prova daquele relatório
     * são o teste `115`.
     *
     * `oculto` pela mesma razão do adensamento: a lista de ensaios de MR
     * já existe, é a Fila do laboratório filtrada por método. Um segundo
     * lugar para ver a mesma coisa é um segundo lugar para ela ficar
     * desatualizada.
     */
    chave: 'mr', titulo: 'Módulo de resiliência', grupo: 'Bancada', ic: '◈',
    espacos: ['labmanager'],
    tabelas: ['ensaio_mr', 'mr_corpo_prova', 'mr_ciclo', 'mr_arquivo'],
    rota: '/ensaio/:id/mr', local: 'oculto', situacao: 'no_ar',
  },

  {
    /*
     * No ar desde 23/08/2026, junto com os limites.
     *
     * A umidade já era CALCULADA dentro da compactação, do ISC e do
     * adensamento desde a 0022 — o que faltava era ela poder ser o ensaio
     * inteiro, que é como a Testesolo mais a executa: a amostra chega, o
     * técnico pesa três cápsulas e o resultado é a média.
     */
    chave: 'umidade', titulo: 'Teor de umidade', grupo: 'Bancada', ic: '◔',
    espacos: ['labmanager'],
    tabelas: ['ensaio_umidade', 'umidade_determinacao'],
    rota: '/ensaio/:id/umidade', local: 'oculto', situacao: 'no_ar',
  },

  {
    /*
     * Dois módulos e um laudo só.
     *
     * LL e LP são ensaios separados na fila — decisão do Humberto em
     * 20/08 — porque quem executa um pode não ser quem executa o outro, e
     * cada um tem sua aprovação. O laudo, esse, sai junto: o índice de
     * plasticidade não pertence a nenhum dos dois, pertence ao par.
     */
    chave: 'limite_liquidez', titulo: 'Limite de liquidez',
    grupo: 'Bancada', ic: '◕',
    espacos: ['labmanager'],
    tabelas: ['ensaio_limite_liquidez', 'limite_liquidez_ponto',
      'limite_liquidez_ajuste'],
    rota: '/ensaio/:id/limite-liquidez', local: 'oculto', situacao: 'no_ar',
  },

  {
    chave: 'limite_plasticidade', titulo: 'Limite de plasticidade',
    grupo: 'Bancada', ic: '◔',
    espacos: ['labmanager'],
    tabelas: ['ensaio_limite_plasticidade',
      'limite_plasticidade_determinacao'],
    rota: '/ensaio/:id/limite-plasticidade', local: 'oculto',
    situacao: 'no_ar',
  },

  {
    /*
     * No ar desde 23/08/2026 — e o cálculo já estava pronto desde a 0022.
     *
     * Este é o caso que o teste 18 existe para expor: a compactação
     * inteira estava no banco, conferida contra a planilha com divergência
     * zero, havia dois dias — e a interface não sabia que ela existia.
     * Um módulo pode ficar semanas só no banco; o que não pode é ninguém
     * conseguir ver que é esse o estado dele.
     */
    chave: 'compactacao', titulo: 'Compactação', grupo: 'Bancada', ic: '◓',
    espacos: ['labmanager'],
    tabelas: ['ensaio_compactacao', 'compactacao_ponto',
      'compactacao_umidade'],
    rota: '/ensaio/:id/compactacao', local: 'oculto', situacao: 'no_ar',
  },

  // ── Saída ─────────────────────────────────────────────────────────
  {
    /*
     * O mapa dos ensaios — um item próprio (onda 177).
     *
     * Nasceu dentro de Relatórios (onda 171) e saiu de lá a pedido:
     *
     *   «O mapa interativo de ensaios deverá ser um item à parte de
     *    relatórios. Ele pode servir mais como uma base de consulta caso
     *    um cliente não encontre uma campanha geotécnica específica. Mas
     *    para utilizá-lo para gerar relatório acredito que não seja o
     *    melhor uso.»                              — Testesolo, 08/09/2026
     *
     * É consulta: «o que já se ensaiou por aqui?». Não governa tabela
     * nenhuma — lê a vista `vw_mapa` (`0171`), que junta amostra, ponto
     * de investigação e obra —, e por isso a lista abaixo é vazia. A
     * permissão é a de ver relatório, que todo perfil da casa tem: o
     * mapa não mostra nada que a ficha da OS já não mostre; só o ONDE.
     *
     * Fica na Saída, e não no Trabalho, porque quem o abre não está
     * lançando nada — está procurando o que já foi entregue.
     *
     * Onda 215: sobe para o bloco de cima, junto do Painel —
     *
     *   «Mapa de ensaios deverá ficar junto com Painel no menu lateral.
     *    Será mais utilizado para consultar campanhas em andamento,
     *    campanhas concluídas e consultar ensaios antigos para ter
     *    conhecimento de alguma região.»       — Humberto, 28/09/2026
     *
     * É consulta do dia, não saída de fim de processo: vai para o bloco
     * sem grupo, que não se esconde atrás de sanfona. Os grupos aprovados
     * (Trabalho · Bancada · Saída · SGQ) não mudam, nem a conta do menu.
     *
     * Onda 293 (os espaços na navegação): passa a se chamar «Mapa
     * Geotécnico» e a morar no Hub (spec 03, §3.3 e §8). A pergunta é a
     * mesma — «o que já se ensaiou por aqui?» —, mas agora ela é da casa
     * inteira (acervo, campo e laboratório), e não só do laboratório.
     */
    chave: 'mapa', titulo: 'Mapa Geotécnico', grupo: null, ic: '◎',
    espacos: ['hub'],
    /* 228: o acervo antes do LabManager (0224, RN-209) mora no mapa. */
    tabelas: ['acervo_obra', 'acervo_ponto', 'acervo_ensaio_lab', 'acervo_documento',
      'acervo_ponto_ensaio', 'acervo_camada', 'acervo_spt'],
    permissoes: ['mapa'],
    rota: '/mapa', raiz: '/mapa', situacao: 'no_ar',
  },
  {
    /*
     * Relatórios volta a «em breve» (onda 177).
     *
     * Entrou no ar pela porta do mapa (onda 171) e o mapa saiu para um
     * item próprio no mesmo dia — ver o comentário logo acima. As
     * tabelas `relatorio`, `relatorio_ensaio` e `relatorio_acesso`
     * continuam esperando o relatório técnico que reúne os ensaios de
     * uma OS (doc 151, lacuna 03); quando vier, entra por esta porta. O
     * item continua no menu, apagado, com a etiqueta do que falta —
     * porque é assim que o mapa do sistema não mente.
     */
    chave: 'relatorios', titulo: 'Relatórios', grupo: 'Saída', ic: '▦',
    espacos: ['labmanager'],
    tabelas: ['relatorio', 'relatorio_ensaio', 'relatorio_acesso'],
    permissoes: ['relatorio', 'documento', 'quadro'],
    /*
     * No ar desde a onda 214: o caderno de laudos (compilado por amostra,
     * por ensaio e total) e o quadro resumo em Excel (vertical,
     * horizontal e SANEPAR), por OS. A emissão de um relatório técnico
     * NOVO, com número próprio, continua sendo a lacuna 03 do doc 151 —
     * ela depende da decisão da Testesolo sobre a numeração.
     */
    rota: '/relatorios', raiz: '/relatorios', situacao: 'no_ar',
  },
  {
    /*
     * O primeiro degrau existe (onda 105): a APURAÇÃO.
     *
     * A ficha da OS mostra, item por item, quanto já foi executado —
     * apurado dos ensaios aprovados, com o caminho de volta até a
     * amostra (§13.2 e §13.5). O que ainda não existe é o BOLETIM: o
     * período, os status da medição, a conferência, a aprovação e o PDF.
     *
     * A onda 107 trouxe o BOLETIM: número automático, o grafo do §13.4
     * com dono em cada passo, a montagem a partir da apuração e a trava
     * que impede medir o mesmo ensaio duas vezes. O módulo entra no ar
     * com a lista e a ficha.
     *
     * O que ainda falta para ele estar completo: o PDF, o ajuste manual
     * com justificativa e o portal do cliente. Estão registrados no
     * documento da onda, e não neste comentário — mapa que vira lista de
     * pendências deixa de ser mapa.
     */
    chave: 'medicao', titulo: 'Medição', grupo: 'Saída', ic: '∑',
    espacos: ['hub'],
    tabelas: ['medicao', 'medicao_item', 'medicao_item_evidencia', 'medicao_os',
      'item_medicao_metodo'],
    permissoes: ['medicao'], rota: '/medicoes', raiz: '/medicao',
    situacao: 'no_ar',
  },
  {
    /*
     * O catálogo é OCULTO no menu, e se chega nele pela Medição.
     *
     * Ele é a lista de preços — o que a casa vende — e ninguém abre o
     * sistema de manhã para consultá-lo. A pergunta que leva alguém até
     * aqui nasce sempre no mesmo lugar: «por que este item apareceu como
     * apuração manual?». Então o caminho é de lá para cá, e não um item
     * de menu a mais.
     *
     * É a opção B do teste `33.1`: o menu não cresce com o catálogo.
     */
    chave: 'itens_medicao', titulo: 'Itens de medição', grupo: 'Saída',
    ic: '≡',
    espacos: ['hub'],
    tabelas: ['item_medicao', 'unidade_medicao', 'regra_apuracao',
      'quantitativo_apurado', 'quantitativo_origem'],
    permissoes: [], rota: '/itens-medicao', local: 'oculto',
    situacao: 'no_ar',
  },
  {
    /*
     * O portal do cliente (0172), visto de dentro.
     *
     * Fica na Saída e não no Sistema porque é TRABALHO, não
     * administração: alguém abre esta tela quando termina um laudo, para
     * entregá-lo. E a pergunta com que ela abre — «o que está pronto e o
     * cliente ainda não recebeu?» — não é respondida por nenhuma outra
     * tela: a fila mostra o que falta ensaiar, a assinatura o que falta
     * aprovar, a medição o que falta medir.
     *
     * As tabelas são todas do portal. `relatorio` e `lote_recebimento`
     * ganharam colunas de publicação na mesma migração, mas continuam
     * sendo dos módulos que já as tinham — uma tabela tem um dono só, e
     * o teste 18.1 recusa duas reivindicações.
     *
     * O que o CLIENTE acessa não é este módulo: é outra aplicação, em
     * outro domínio, com outro usuário de banco. Aqui está só a porta
     * pela qual a Testesolo decide o que vai para lá.
     */
    chave: 'portal', titulo: 'Portal do cliente', grupo: 'Saída', ic: '⇱',
    espacos: ['hub'],
    tabelas: ['portal_pessoa', 'portal_pessoa_cliente', 'portal_codigo',
      'portal_sessao', 'portal_acesso', 'portal_tabela_permitida'],
    permissoes: ['portal'],
    rota: '/portal', situacao: 'no_ar',
  },

  // ── SGQ ───────────────────────────────────────────────────────────
  //
  // "Teremos que agrupar uma seção chamada SGQ, contendo: Calibração de
  // equipamentos / Padrões / Normas / Demais itens que concordar"
  // (Testesolo, 23/08/2026). Os "demais itens" são os capítulos do item
  // 17 do briefing que ainda não existem — ficam listados apagados, com
  // a etiqueta do que falta, porque é assim que o mapa não mente.
  {
    chave: 'equipamentos', titulo: 'Equipamentos e calibrações',
    grupo: 'SGQ', ic: '⚙',
    espacos: ['hub', 'labmanager', 'campo'],
    tabelas: ['equipamento', 'equipamento_valor', 'verificacao_interna',
      'verificacao_item', 'verificacao_item_criterio', 'verificacao_item_leitura',
      'calibracao_externa', 'certificado_interno', 'certificado_padrao',
      'curva_calibracao', 'curva_calibracao_ponto',
      // As versões de software e firmware do equipamento (0275).
      'equipamento_software',
      // A curva como MEDIDA na aferição (0100). Está para a
      // `curva_calibracao` como `verificacao_item` está para
      // `equipamento_valor`: uma é o que se mediu, a outra é o que
      // passou a valer quando alguém assinou.
      'verificacao_curva', 'verificacao_curva_ponto',
      // As colunas que a 0102 protege: mexer nelas exige motivo, porque
      // é por elas que a RN-154 decide a que ensaio o equipamento serve.
      'equipamento_campo_metrologico',
      // Onde π·d²/4·h faz sentido (0106). O Casagrande tem diâmetro e
      // altura e não é um cilindro — a lista existe por causa dele.
      'categoria_cilindrica',
      // Onde a seção é L₁ × L₂ (0233): o anel de cisalhamento, que é
      // quadrado (RN-224) — área L₁·L₂ e volume L₁·L₂·h.
      'categoria_prismatica',
      // Fotografia e manutenção (`0053`). Ficam neste módulo e não num
      // próprio: quem responde pela manutenção é quem responde pelo
      // equipamento, e um cadeado separado deixaria alguém com acesso à
      // parada de três dias sem acesso ao equipamento que parou.
      'equipamento_foto', 'equipamento_manutencao',
      // De quem é o equipamento, e desde quando (0093). Fica aqui, e não
      // num módulo comercial: quem responde «posso usar esta prensa?»
      // precisa responder na mesma tela «e ela é nossa?».
      'equipamento_posse', 'equipamento_regime',
      // As tolerâncias da NBR NM ISO 3310-1 (0196): o critério com que a
      // aferição da peneira é julgada, e a fronteira da RN-197.
      'peneira_tolerancia'],
    permissoes: ['equipamento'],
    rota: '/equipamentos', raiz: '/equipamento', situacao: 'no_ar',
  },
  {
    chave: 'padroes', titulo: 'Padrões', grupo: 'SGQ', ic: '⌾',
    espacos: ['hub', 'labmanager', 'campo'],
    // Padrão é `equipamento` com papel = 'padrao' (0030). Não há tabela
    // própria de propósito: o ciclo de vida é o mesmo, e duplicá-lo seria
    // duplicar cada regra de calibração.
    tabelas: [],
    rota: '/padroes', situacao: 'no_ar',
    /*
     * Fora do menu desde a onda 208:
     *
     *   «Elimine a aba do menu lateral "Padrões" e coloque como uma
     *    categoria como Máquinas em "Equipamentos e calibrações"»
     *   (Humberto, 22/09/2026).
     *
     * O padrão é a aba «Padrões» da tela do parque, ao lado de Máquinas,
     * Instrumentos e Itens aferidos. A rota `/padroes` continua, e leva
     * a essa aba — é a mesma lista, com o mesmo cadeado.
     */
    local: 'oculto', dentroDe: 'equipamentos',
  },
  {
    chave: 'normas', titulo: 'Normas e métodos', grupo: 'SGQ', ic: '§',
    espacos: ['hub'],
    // O catálogo que a 0032 carregou: 99 normas em 122 edições. Sem tela,
    // ele existia só para quem abre o banco — que é exatamente o
    // descompasso que este arquivo existe para não deixar acontecer.
    tabelas: ['norma', 'norma_edicao', 'norma_errata'],
    /*
     * `competencia` entra AQUI, e não numa coluna própria na matriz de
     * acessos.
     *
     * A matriz cresce uma coluna por módulo com permissões, e cada
     * coluna custa uns 40 px: com uma coluna a mais ela passou a rolar
     * de lado num notebook de 1440 px, e o teste `51.6` acusou. O CSS
     * dela já dizia o que fazer quando não coubesse mais — «a saída é
     * AGRUPAR módulos numa coluna só, e não continuar espremendo» —, e é
     * isto.
     *
     * O agrupamento não é arbitrário: a competência autoriza uma pessoa
     * para um MÉTODO, e é este módulo que governa o catálogo de métodos.
     * O cadeado é um só porque o objeto é o mesmo.
     */
    permissoes: ['cadastro', 'competencia'],
    rota: '/normas', raiz: '/norma', situacao: 'no_ar',
  },
  {
    /*
     * A `RN-121` existe desde a `0001` e não tinha porta: a tabela
     * `metodo_versao_calculo` estava vazia e nenhuma rota a preenchia,
     * então o primeiro ensaio de cada método morria no clique de
     * aprovar. Este módulo É a porta (0087).
     */
    chave: 'calculos', titulo: 'Métodos homologados', grupo: 'SGQ', ic: '∑',
    espacos: ['hub'],
    tabelas: ['metodo_versao_calculo', 'metodo_calculo_proposta',
              'metodo_campo'],
    permissoes: ['cadastro', 'metodo'],
    rota: '/calculos', raiz: '/calculo', situacao: 'no_ar',
  },
  {
    chave: 'nao_conformidades', titulo: 'Não conformidades', grupo: 'SGQ',
    /*
     * Quatro nomes do §17 — não conformidade, ação corretiva, trabalho
     * não conforme e reclamação — numa entrada só, porque o CAMINHO é o
     * mesmo (17025 §7.9, §7.10 e §8.7): registra, contém, procura a
     * causa, age, confere a eficácia, encerra.
     */
    ic: '⚠',
    espacos: ['hub'],
    tabelas: ['nao_conformidade', 'nao_conformidade_acao',
      'nao_conformidade_sequencia'],
    permissoes: ['relato'],
    rota: '/nao-conformidades', raiz: '/nao-conformidades',
    situacao: 'no_ar',
  },
  {
    chave: 'documentos', titulo: 'Documentos', grupo: 'SGQ',
    espacos: ['hub'],
    /*
     * Ainda sem tela, mas o item apagado do menu também segue o alcance: o
     * Hub de quem só tem o «Ver» do Mapa é um item só (spec 03 §11, critério
     * 9). Segue o cadastro, como «Normas e métodos» e «Métodos homologados»,
     * que são do mesmo grupo.
     */
    permissoes: ['cadastro'],
    ic: '▤', tabelas: [], rota: null, situacao: 'fase_2',
  },
  {
    chave: 'competencias', titulo: 'Competências', grupo: 'SGQ',
    espacos: ['hub'],
    /*
     * A trava do §17 está no banco desde a onda 65 e a `competencia`
     * nunca teve porta: a permissão `competencia.conceder` existia sem
     * governar nada, e quem ligasse a trava num método pararia a bancada
     * sem ter como destravá-la. A tela é a porta.
     */
    /*
     * As `permissoes` seguem as de «Normas e métodos», que governa o
     * cadeado (`competencia.conceder`): quem alcança um, alcança o outro.
     * O menu de um espaço alcançado só mostra o que a pessoa alcança (H1).
     */
    permissoes: ['cadastro', 'competencia'],
    ic: '☑', tabelas: ['competencia'], rota: '/competencias',
    situacao: 'no_ar',
  },
  {
    chave: 'treinamentos', titulo: 'Treinamentos', grupo: 'SGQ',
    /*
     * «O treinamento existe antes da trava» (doc 55) — e desde a onda
     * 182 existe no sistema: tema, carga horária, ementa, ministrante,
     * métodos cobertos e a lista de presença com nota. Concluir concede
     * a competência aos aprovados e emite o certificado. É a evidência
     * que a competência sempre pediu e nunca tinha onde nascer.
     *
     * Governado pelo mesmo cadeado de «Normas e métodos»
     * (`competencia.conceder`): concluir um treinamento É conceder.
     */
    ic: '✎',
    espacos: ['hub'],
    tabelas: ['treinamento', 'treinamento_metodo', 'treinamento_participante',
      'treinamento_sequencia'],
    rota: '/treinamentos', raiz: '/treinamentos', situacao: 'no_ar',
    /*
     * Fora do menu, atrás da porta de «Competências».
     *
     * A onda 182 pôs Treinamentos no menu e o menu foi a vinte e um
     * itens — um acima do teto do `33.1`, que é o ponto em que ele
     * deixa de caber no tablet em pé sem rolar. O teto sobe quando há
     * motivo escrito; aqui não há: treinamento e competência são o
     * mesmo assunto visto de dois lados — concluir um treinamento é
     * conceder competência, e as duas telas já se apontam uma à outra
     * desde que nasceram. Dois itens de menu para um assunto só é a
     * mesma recaída que a opção B (23/08/2026) evita no catálogo.
     *
     * Não se perde caminho: a tela de Competências já tinha o botão
     * «Treinamentos», a rota continua registrada e a trilha (onda 188)
     * passa a mostrar Competências antes dele, que é por onde se chega.
     */
    local: 'oculto', dentroDe: 'competencias',
  },
  {
    /*
     * Quem calibra os nossos equipamentos.
     *
     * «Os fornecedores de calibração devem ser homologados antes de
     *  poderem prestar serviço» (Testesolo, 01/09/2026). A 17025 chama
     * isso de avaliar e aprovar provedores externos (§6.6).
     *
     * No SGQ e não na engrenagem: ao contrário do catálogo de
     * categorias, aqui há trabalho recorrente — avaliar, aprovar,
     * reavaliar quando a aprovação vence, atender à auditoria que pede
     * o registro. É registro da qualidade, e quem cuida do SGQ abre
     * isto no turno dele.
     */
    /*
     * O campo numa entrada só, com três abas: diários, equipes e
     * veículos.
     *
     * Separá-los custaria três linhas do menu para responder a uma
     * pergunta só — quem coordena campo olha o dia e escolhe a equipe na
     * mesma conversa. Os pontos de investigação NÃO entram aqui: eles
     * pendem da ordem de serviço e se chega neles pela ficha da OS, que é
     * onde a pessoa já está.
     */
    chave: 'campo', titulo: 'Campo', grupo: 'Trabalho', ic: '⛏',
    espacos: ['campo'],
    tabelas: ['equipe', 'equipe_membro', 'veiculo', 'ponto_investigacao',
      'diario_campo', 'diario_paralisacao', 'diario_ponto',
      /*
       * A instrumentação (`0143`) mora no módulo de campo, e não num
       * módulo próprio, porque é o mesmo trabalho visto no tempo longo:
       * o instrumento nasce num PONTO de uma campanha e é lido por
       * equipes de campo, em dias de campo, por anos. Um item de menu
       * separado custaria uma linha do menu — que tem teto (`33.1`) —
       * para responder a uma pergunta que já se faz aqui.
       */
      'instrumento_tipo', 'instrumento', 'instrumento_leitura',
      /* 362 · a referência de nível (RN) da OS, para todos os ensaios de campo (NBR 6484 5.2.1). */
      'campo_referencia_nivel'],
    permissoes: ['campo'],
    rota: '/campo', raiz: '/campo', situacao: 'no_ar',
  },
  {
    /*
     * O Campo de UMA ordem de serviço: furos e diários, que saíram da
     * ficha da OS (a aba «Campo» do `OsCampo`).
     *
     * É um módulo à parte, e não o `os`, porque a tela é do Campo: o
     * endereço `/os/:id/campo` mora no espaço Campo (spec 03 §8) e quem não
     * alcança o Campo vê a recusa dele, e não a ficha da OS. O
     * `moduloDoEndereco` dá peso pelo tamanho da rota, então esta, mais
     * longa, ganha de `/os` — o mesmo molde de «Programar ensaios». Não é
     * item de menu (precisa do id da OS) e chega-se a ela pela ficha da OS.
     */
    chave: 'campo_da_os', titulo: 'Campo da OS', grupo: 'Trabalho', ic: '⛏',
    espacos: ['campo'],
    tabelas: [],
    permissoes: ['campo'],
    rota: '/os/:id/campo', local: 'oculto', dentroDe: 'campo',
    situacao: 'no_ar',
  },
  {
    /*
     * As campanhas CPTu — onda 312 (spec 07; RN-253; R1).
     *
     * A campanha de piezocone de uma OS, da importação dos arquivos do
     * equipamento ao relatório: as verticais (cada uma um ponto da OS), as
     * leituras, as dissipações e as fotos. É item de menu, e não porta de
     * outra tela, porque é trabalho do dia de quem interpreta campo — o RT
     * e o gestor de campo abrem a lista para saber o que falta importar,
     * verificar e emitir.
     *
     * Mora no Campo e no Hub (R1): no Campo, é o trabalho; no Hub, é a
     * consulta da casa inteira. O teto do `33.1` é por espaço desde a 312
     * (o menu lateral mostra um espaço só desde a 293).
     *
     * `ic` vazio de propósito: o ícone é o desenho `cone` do `Icone.tsx`
     * (P-UI-43, onda 308), que a barra e a folha «Mais» do celular já usam;
     * no menu do PC ele chega com os ícones SVG do PC — um glifo novo
     * subiria a catraca `ui.icone_caractere_no_menu`. Até lá, a folha da
     * campanha desenha o cone (bloco 312 do `estilo.css`).
     */
    chave: 'cptu', titulo: 'Campanhas CPTu', grupo: 'Trabalho', ic: '',
    espacos: ['campo', 'hub'],
    tabelas: ['cptu_campanha', 'cptu_vertical', 'cptu_leitura', 'cptu_dissipacao',
      'cptu_dissipacao_leitura', 'cptu_foto', 'cptu_modelo_importacao', 'cptu_camada', 'cptu_emissao'],
    permissoes: ['cptu'],
    rota: '/cptu', raiz: '/cptu', situacao: 'no_ar',
  },
  {
    /**
     * A sondagem SPT — ondas 355–359 (spec 12, RN-276). O boletim transcrito
     * do papel no escritório, o perfil, o boletim em PDF e o relatório da
     * campanha. Mora no Campo e no Hub, como o CPTu. Fica `em_breve` até a
     * 356 trazer a tela.
     */
    chave: 'spt', titulo: 'Sondagens SPT', grupo: 'Trabalho', ic: '',
    espacos: ['campo', 'hub'],
    tabelas: ['spt_campanha', 'spt_furo', 'spt_metro', 'spt_camada', 'spt_avanco',
      'spt_na_leitura', 'spt_fenomeno', 'spt_furo_recomendacao', 'spt_infiltracao',
      'spt_material', 'spt_complemento', 'spt_plasticidade', 'spt_cor', 'spt_genese',
      'spt_estado', 'spt_criterio_paralisacao', 'spt_tipo_fenomeno', 'spt_recomendacao'],
    permissoes: ['spt'],
    rota: '/spt', raiz: '/spt', situacao: 'no_ar',
  },

  // ── Cadastros ─────────────────────────────────────────────────────
  //
  // «O cadastro de itens está ocupando muito do menu lateral. Acredito
  //  que deva ter um local centralizado para cadastrar coisas, como
  //  cliente, fornecedores de calibração, locais de armazenamento»
  // (Humberto, 08/09/2026).
  //
  // Um item só no menu, e uma tela de cartões atrás dele. Cada cadastro
  // continua sendo módulo próprio — declara as suas tabelas, tem a sua
  // rota e o seu cadeado —, mas fica `oculto` com `dentroDe: 'cadastros'`,
  // e é a porta que o lista. O menu não cresce com o catálogo de
  // cadastros, que é o mesmo princípio dos métodos na Bancada (`33.1`).
  //
  // O que NÃO veio para cá, e por quê: «Normas e métodos» e «Contas dos
  // métodos» ficam no SGQ porque são registro da qualidade, não cadastro
  // de apoio; «Categorias do parque» fica na engrenagem porque é
  // configuração de administrador (01/09); equipes e veículos ficam em
  // Campo porque quem os cadastra é quem programa o dia de campo. A tela
  // da porta aponta para todos eles, para ninguém procurar cadastro e
  // não achar.
  {
    chave: 'cadastros', titulo: 'Cadastros', grupo: 'Trabalho', ic: '▥',
    espacos: ['hub'],
    /*
     * `tabelas` vazio de propósito: a porta não é dona de dado nenhum.
     * Cada cadastro que ela abre responde pelas próprias tabelas, e o
     * registro exige um dono só por tabela.
     */
    tabelas: [],
    permissoes: ['cadastro'],
    rota: '/cadastros', situacao: 'no_ar',
  },
  {
    /*
     * Clientes, obras e contatos — o cadastro que faltava.
     *
     * A API lista, altera e inativa cliente desde a `0001`, e até
     * 08/09/2026 a interface só tinha o cadastro RÁPIDO, embutido no
     * recebimento e na abertura da OS (`cadastros.tsx`). Ele resolve o
     * beco de quem chega ao balcão com material de cliente novo, e
     * continua resolvendo — mas não responde «quais clientes temos?»,
     * «este CNPJ está certo?» nem «quem é o contato desta obra?». Esta
     * tela responde.
     *
     * As três tabelas ficam FORA da varredura do teste `18.1` (são
     * cadastro de apoio, não trabalho de laboratório); declará-las aqui
     * mesmo assim é o que faz o registro dizer a verdade sobre quem
     * responde por elas.
     */
    chave: 'clientes', titulo: 'Clientes e obras', grupo: 'Trabalho',
    ic: '◪',
    espacos: ['hub'],
    tabelas: ['cliente', 'obra', 'contato'],
    permissoes: ['cadastro'],
    rota: '/clientes', raiz: '/cliente', local: 'oculto',
    dentroDe: 'cadastros',
    resumo: 'Razão social, CNPJ, obras de cada cliente e os contatos '
      + 'de cada obra. É de onde a OS e o recebimento puxam o nome.',
    situacao: 'no_ar',
  },
  {
    chave: 'fornecedores', titulo: 'Fornecedores de calibração',
    /*
     * Saiu do SGQ para dentro de «Cadastros» em 08/09/2026, a pedido do
     * Humberto. O trabalho recorrente de avaliar e aprovar (17025 §6.6)
     * continua na tela — o que mudou foi o caminho até ela.
     */
    grupo: 'SGQ', ic: '⛭',
    espacos: ['hub'],
    tabelas: ['fornecedor_calibracao', 'grandeza_calibracao'],
    permissoes: ['cadastro'],
    rota: '/fornecedores', local: 'oculto', dentroDe: 'cadastros',
    resumo: 'Quem calibra os nossos equipamentos e padrões, com a '
      + 'homologação que a 17025 exige antes do primeiro serviço.',
    situacao: 'no_ar',
  },
  {
    /*
     * Nasceu na onda 24, e nasceu por um número: em homologação, 10 de 10
     * amostras diziam "sem local registrado". A coluna não estava
     * quebrada — não havia onde escolher o lugar, porque não havia
     * cadastro de lugares.
     *
     * Ficou em "Trabalho" como item próprio até 08/09/2026, com o
     * argumento de que quem procura a prateleira 3 é o técnico com a
     * amostra na mão. O argumento continua certo — e é por isso que o
     * técnico chega ao local pela AMOSTRA (a tela dela mostra onde está
     * e deixa mover), não por este cadastro. Este cadastro é o que se
     * abre para criar a prateleira, uma vez; então mora em «Cadastros».
     */
    chave: 'locais', titulo: 'Locais de armazenamento', grupo: 'Trabalho',
    ic: '▤',
    espacos: ['hub'],
    tabelas: ['local_armazenamento', 'tipo_local_armazenamento'],
    permissoes: ['cadastro'],
    rota: '/locais', raiz: '/local', local: 'oculto', dentroDe: 'cadastros',
    resumo: 'Prateleiras, câmaras úmidas e bancadas — os lugares que a '
      + 'coluna «onde está» da amostra oferece.',
    situacao: 'no_ar',
  },

  // ── Comercial (Plano 2 do ERP) ────────────────────────────────────
  {
    /*
     * 0500 (onda 347) — a caixa de entrada do Comercial: os canais, as
     * conversas, as mensagens, o contato de vários clientes, as rotas, os
     * números internos e as respostas prontas. Só o banco por ora: a tela
     * nasce na Tarefa 14 do Plano 2, quando o espaço Comercial abre
     * (`espaco.de_trabalho`). Até lá, `em_breve` e sem rota.
     *
     * `ic` vazio de propósito, como o do `cptu`: o ícone é o desenho
     * `receber` do `Icone.tsx` (P-UI-43); um glifo novo subiria a catraca
     * `ui.icone_caractere_no_menu`.
     *
     * 0504 (onda 549) — a fila de mensagens: `fila_entrada` (o que o
     * WhatsApp e o e-mail trouxeram) e `fila_saida` (o que sai, no ritmo do
     * canal). Quem grava é a mensageria, pelas funções do banco; a caixa
     * mostra a entrega de cada mensagem.
     *
     * 0505 (onda 549) — a `transcricao` dos áudios, que a mensageria faz
     * (whisper.cpp) e a caixa mostra embaixo do áudio. O chat interno
     * (Plano 3) usa a mesma tabela, pelo prefixo `chat/`.
     *
     * 0507 (onda 550, Tarefa 14) — a tela: a lista em `/comercial/caixa` e a
     * conversa aberta em `/comercial/conversa/:id` (a raiz), a mesma tela em
     * três colunas (lista, conversa e o painel do cliente). O espaço abre.
     */
    chave: 'caixa', titulo: 'Caixa de entrada', grupo: 'Trabalho', ic: '',
    espacos: ['comercial'],
    tabelas: ['canal', 'canal_estado', 'rota', 'conversa',
      'mensagem', 'numero_interno', 'resposta_pronta',
      'fila_entrada', 'fila_saida', 'fila_saida_tentativa', 'transcricao',
      /* 0528 (Tarefa 16b): a marca «comercial» da mensagem. */
      'mensagem_comercial'],
    permissoes: ['atendimento', 'comercial', 'canal'],
    rota: '/comercial/caixa', raiz: '/comercial/conversa', situacao: 'no_ar',
  },
  {
    /*
     * 0501 (onda 347) — o CRM: a oportunidade (o lead é a oportunidade sem
     * cliente), o histórico do funil, as tarefas e as propostas registradas
     * (as feitas em Word, com o PDF). Só o banco por ora: a tela nasce na
     * Tarefa 15 do Plano 2, com a unidade `crm`. Até lá, `em_breve` e sem
     * rota. O ícone é o `filtro` (o funil) do `Icone.tsx`.
     *
     * 0508 (onda 550, Tarefa 15) — a tela e a unidade `crm`: a lista em
     * `/comercial/oportunidades` (cada um vê as próprias e as indicadas; a
     * diretoria, todas) e a ficha em `/comercial/oportunidade/:id` (a raiz),
     * com o histórico do cartão, as propostas feitas em Word com o PDF, as
     * tarefas e as conversas. A tarefa ganhou módulo próprio (`tarefas`).
     */
    chave: 'oportunidades', titulo: 'Oportunidades', grupo: 'Trabalho', ic: '',
    espacos: ['comercial'],
    tabelas: ['oportunidade', 'oportunidade_movimento', 'proposta_registrada'],
    permissoes: ['crm', 'cliente'],
    rota: '/comercial/oportunidades', raiz: '/comercial/oportunidade', situacao: 'no_ar',
  },
  {
    /*
     * 0508 (onda 550, Tarefa 15) — os contatos do Comercial: o contato de
     * vários clientes, com o papel em cada um (spec §5.3 item 4), as
     * oportunidades e as conversas dele. A lista em `/comercial/contatos` e
     * a ficha em `/comercial/contato/:id`. É da unidade Atendimento (0500:
     * «contatos»), e segue a carteira (`fn_contato_visivel`).
     */
    chave: 'contatos', titulo: 'Contatos', grupo: 'Trabalho', ic: '',
    espacos: ['comercial'],
    tabelas: ['contato_cliente'],
    permissoes: ['atendimento'],
    rota: '/comercial/contatos', raiz: '/comercial/contato', situacao: 'no_ar',
  },
  {
    /*
     * 0501/0508 (onda 550, Tarefa 15) — as tarefas do Comercial: as minhas,
     * as vencidas, feita e cancelada com motivo. As automáticas de
     * acompanhamento (D+2, D+7, 2 dias antes de vencer) chegam na Tarefa 24.
     */
    chave: 'tarefas', titulo: 'Tarefas', grupo: 'Trabalho', ic: '',
    espacos: ['comercial'],
    tabelas: ['tarefa'],
    permissoes: ['crm'],
    rota: '/comercial/tarefas', situacao: 'no_ar',
  },
  {
    /*
     * 0509 (onda 550, Tarefa 17) — o funil anda sozinho: os eventos do
     * sistema (`funil_evento`, spec §8.3) movem o cartão pela
     * `fn_funil_evento`, com a origem e o evento no histórico; o relógio
     * leva a proposta vencida sem retorno a Perdida.
     *
     * Onda 550, Tarefa 18 — a tela: o quadro em colunas em `/comercial/funil`
     * (spec §8.3), com o movimento à mão só com o motivo (arrastar ou «Mover
     * para…»), os filtros de responsável (a diretoria), linha e período, e
     * ao vivo por `comercial.funil`.
     */
    chave: 'funil', titulo: 'Funil', grupo: 'Trabalho', ic: '',
    espacos: ['comercial'],
    tabelas: ['funil_evento'],
    permissoes: ['crm'],
    rota: '/comercial/funil', situacao: 'no_ar',
  },
  {
    /*
     * 0510 (onda 550, Tarefa 19) — o Radar (spec §8.6): as conversas sem a
     * nossa resposta ou à espera do cliente, as propostas paradas sem próxima
     * ação e as tarefas vencidas, em crítico, em risco e em andamento, com o
     * que falta e quem age (`fn_radar`). Os limites são ajustes do sistema
     * (`radar_*`). Abre para quem atende ou cuida das oportunidades; quem só
     * atende vê as conversas. Sem tabela própria.
     */
    chave: 'radar', titulo: 'Radar', grupo: 'Trabalho', ic: '',
    espacos: ['comercial'],
    tabelas: [],
    permissoes: ['atendimento', 'crm'],
    rota: '/comercial/radar', situacao: 'no_ar',
  },
  {
    /*
     * 0531 (onda 552, Tarefa 28) — o painel do Comercial (spec §8.4): a
     * primeira resposta em minutos corridos (a meta 1.1), o pedido à
     * proposta, a conversão, o valor em proposta aberta, o funil, os motivos
     * de perda, as sugestões da IA sem correção e o gasto da IA do mês, com o
     * Radar embutido no «Hoje» (`fn_painel_comercial`). A mesma porta do
     * Radar; cada um vê os próprios números, e os valores são de quem tem o
     * Comercial (P2-22). Sem tabela própria.
     */
    chave: 'painel_comercial', titulo: 'Painel do Comercial', grupo: 'Trabalho', ic: '',
    espacos: ['comercial'],
    tabelas: [],
    permissoes: ['atendimento', 'crm'],
    rota: '/comercial/painel', situacao: 'no_ar',
  },
  {
    /*
     * 0506 (onda 550) — os ajustes do Comercial: o horário comercial (de
     * onde saem os 30 minutos úteis do substituto), a regra simples de
     * estimativa do roteamento e os passos do relógio. A tela é da Tarefa
     * 16 (onda 550): `/comercial/ajustes`, no grupo Ajustes, com os canais
     * (o QR ao vivo), as rotas, os números internos, as respostas prontas, os
     * estratégicos, os contatos de WhatsApp repetidos e as entradas que
     * falharam; a 0526 traz o pedido de desconectar o aparelho
     * (`canal_desconexao`).
     */
    chave: 'ajustes_comercial', titulo: 'Ajustes do Comercial', grupo: 'Ajustes', ic: '',
    espacos: ['comercial'],
    tabelas: ['comercial_horario', 'estimativa_regra', 'relogio_passo', 'canal_desconexao'],
    permissoes: ['atendimento'],
    rota: '/comercial/ajustes', situacao: 'no_ar',
  },
  {
    /*
     * 0511 (onda 552) — o agente de IA: a configuração (ligado, fase, aviso
     * de atendimento automatizado, política de privacidade), as decisões que
     * ele registra a cada ação, o uso e o preço do provedor (o teto de gasto)
     * e a lista das tabelas em que ele só escreve com decisão aberta
     * (RN-473). O ícone é o `verificar` do `Icone.tsx`.
     * 0512 (onda 552, Tarefa 22) — a base de conhecimento do agente, com
     * versão aprovada pela diretoria, e as lacunas (o que a base não respondeu).
     * 0513 (onda 552, Tarefa 24) — a tela `/comercial/agente` (Situação,
     * Decisões, Base e Lacunas), o que a pessoa fez com cada sugestão
     * (`sugestao_resolucao`) e o mês em que o teto chegou a 80 %
     * (`agente_teto_aviso`).
     * 0514 (onda 552, Tarefa 25) — a indicação de um lead por um número
     * interno (`indicacao`): lida, confirmada com quem indicou e registrada.
     */
    chave: 'agente', titulo: 'Agente', grupo: 'Trabalho', ic: '',
    espacos: ['comercial'],
    tabelas: ['agente_config', 'decisao_agente', 'agente_uso', 'agente_preco', 'tabela_do_agente',
      'base_artigo', 'base_lacuna', 'sugestao_resolucao', 'agente_teto_aviso', 'indicacao'],
    permissoes: ['atendimento'],
    rota: '/comercial/agente', situacao: 'no_ar',
  },
  {
    /*
     * 0503 (onda 548) — os avisos: o sino da moldura e a lista `/avisos`,
     * com as preferências de cada pessoa e o push neste aparelho.
     *
     * `oculto`: chega-se pelo sino do topo, que está em toda tela, e não por
     * um item de menu. `permissoes: []`: o aviso é da pessoa, todo mundo com
     * sessão tem os seus. Mora no Hub (spec do Comercial, §4.5: «Hub ·
     * início, tarefas, avisos») e também nos outros espaços: o sino está em
     * todos, e quem só alcança o LabManager, o Campo ou o Comercial não pode
     * dar com a recusa do Hub ao tocar nele.
     */
    chave: 'avisos', titulo: 'Avisos', grupo: null, ic: '',
    espacos: ['hub', 'labmanager', 'campo', 'comercial'],
    tabelas: ['aviso_tipo', 'aviso', 'aviso_preferencia', 'push_inscricao', 'aviso_destino_perfil', 'estufa_aviso_pessoa'],
    permissoes: [], rota: '/avisos', local: 'oculto', situacao: 'no_ar',
  },

  // ── Engrenagem ────────────────────────────────────────────────────
  //
  // O critério: fica no menu o que você FAZ; vai para a engrenagem o que
  // você CONFIGURA. Nenhum dos dois abaixo produz registro técnico, e
  // ninguém abre nenhum dos dois num turno normal de bancada.
  {
    chave: 'usuarios', titulo: 'Usuários e acessos', grupo: 'Sistema', ic: '⌸',
    espacos: ['sistema'],
    tabelas: ['usuario', 'usuario_perfil', 'usuario_permissao',
      'perfil', 'perfil_permissao', 'permissao', 'acesso', 'espaco',
      /* 0511 (onda 552): o hash da chave de serviço de cada conta de serviço. */
      'usuario_servico_chave'],
    /*
     * `exportacao` entra AQUI, e não num módulo próprio (`0145`).
     *
     * Duas razões. A primeira é de significado: quem decide quem alcança
     * o quê é este módulo, e a exportação é a porta que alcança tudo de
     * uma vez — o cadeado é o mesmo assunto. A segunda é de largura: a
     * matriz de acessos cresce uma coluna por módulo com `permissoes`, e
     * o CSS dela tem escrito o que fazer quando não couber mais — «a
     * saída é AGRUPAR módulos numa coluna só, e não continuar
     * espremendo» (`51.6`).
     */
    permissoes: ['usuario', 'exportacao', 'ajuste'],
    rota: '/usuarios', local: 'engrenagem', situacao: 'no_ar',
  },
  {
    /*
     * «Os dados são da Testesolo» — a tela que prova a frase (§20).
     *
     * Na engrenagem, e não no menu: ninguém exporta o banco num turno de
     * bancada. Módulo próprio porque ele tem TABELA própria e o registro
     * exige um dono só por tabela — mas sem `permissoes`, porque os
     * cadeados dele moram no módulo de acessos, logo acima.
     */
    /*
     * Os ajustes do sistema (onda 290, 0244): os parâmetros que a
     * Testesolo muda sem código — o primeiro é a janela de correção do
     * próprio lançamento. Na engrenagem: configura, não produz registro
     * técnico. Sem `permissoes`, como a exportação: o cadeado
     * (`ajuste.alterar`) mora na unidade de Usuários e acessos.
     */
    chave: 'ajustes', titulo: 'Ajustes do sistema',
    grupo: 'Sistema', ic: '⚙',
    espacos: ['sistema'],
    tabelas: ['ajuste_do_sistema'],
    rota: '/ajustes', local: 'engrenagem', situacao: 'no_ar',
  },
  {
    chave: 'exportacao', titulo: 'Exportação dos dados',
    grupo: 'Sistema', ic: '⤓',
    espacos: ['sistema'],
    tabelas: ['exportacao'],
    rota: '/exportacao', local: 'engrenagem', situacao: 'no_ar',
  },
  {
    /*
     * O catálogo que define o que É uma balança.
     *
     * «O cadastro de categorias de equipamentos deveria ser em outro
     *  local (com acesso apenas para administradores)» (Testesolo,
     * 01/09/2026). Vivia numa janela modal dentro da busca do parque,
     * ao lado de «Novo equipamento» — quem passa o dia procurando
     * balança esbarrando no catálogo que define o que é uma balança.
     *
     * Cai no mesmo critério de 23/08: no menu o que você FAZ, na
     * engrenagem o que você CONFIGURA. Não produz registro técnico e
     * ninguém o abre num turno de bancada.
     */
    chave: 'categorias_equipamento', titulo: 'Categorias do parque',
    grupo: 'Sistema', ic: '⧉',
    espacos: ['sistema'],
    tabelas: ['categoria_equipamento'],
    permissoes: ['cadastro'],
    rota: '/categorias-de-equipamento', local: 'engrenagem', situacao: 'no_ar',
  },
  {
    /*
     * Saiu do menu lateral em 23/08/2026 e o botão flutuante FICOU —
     * foi a condição que a Testesolo pôs, e é a que importa. O canal de
     * retorno precisa estar na tela onde o defeito aparece; a LISTA dos
     * relatos é triagem, e triagem é administração.
     */
    chave: 'relatos', titulo: 'Relatos do beta', grupo: 'Sistema', ic: '✎',
    espacos: ['sistema'],
    tabelas: ['relato_beta'],
    permissoes: ['relato'],
    rota: '/relatos', local: 'engrenagem', situacao: 'no_ar',
  },
  {
    /*
     * 0211 (RN-214) — a exclusão definitiva, só na homologação.
     *
     * «Crie essa opção exclusivamente para o meu perfil Admin (Humberto
     *  Kuszkowski)» (02/10/2026). `oculto` de propósito: a engrenagem só a
     * desenha quando o `/auth/eu` diz `exclusaoDefinitiva` — a marca da
     * pessoa, a chave da instalação, e nenhuma persona vestida. Sem
     * tabela própria: ela APAGA das outras.
     */
    chave: 'exclusao_definitiva', titulo: 'Exclusão definitiva',
    grupo: 'Sistema', ic: '⌫',
    espacos: ['sistema'],
    tabelas: [],
    rota: '/exclusao-definitiva', local: 'oculto', situacao: 'no_ar',
  },
  {
    /*
     * A vitrine do Design Moderno (onda 321, RN-258, D4 de 18/10): cada peça
     * da base em cada estado — a referência de quem faz tela, na bancada, no
     * Portal e no ERP. Na engrenagem, aberta a todo usuário interno: não
     * produz registro e não mostra dado de cliente. Sem tabela.
     */
    chave: 'padrao', titulo: 'O padrão visual', grupo: 'Sistema', ic: '◇',
    espacos: ['sistema'],
    tabelas: [],
    rota: '/padrao', local: 'engrenagem', situacao: 'no_ar',
  },
];

/**
 * Os espaços de trabalho, na ordem e com a grafia da faixa:
 * «Hub · LabManager · Campo · Comercial».
 *
 * Quem ALCANÇA cada espaço vem da sessão (`Eu.espacos`, calculado no banco
 * pela tabela `espaco` e pelas unidades de acesso — spec 03 §9). Esta lista
 * fica no código porque é o registro que diz em que espaço cada TELA mora;
 * o teste `282.30` confere que ela e a tabela dizem os mesmos espaços, com
 * os mesmos nomes e na mesma ordem (D12). Espaço novo entra nos dois.
 *
 * `descricao` é a linha que o seletor de espaço (onda 346) mostra sob o nome:
 * uma frase curta, sem ponto final, que diz o que a pessoa encontra lá. Só o
 * seletor a usa; o banco não a guarda.
 */
export const ESPACOS: { chave: EspacoDeTrabalho; nome: string; descricao: string }[] = [
  { chave: 'hub', nome: 'Hub', descricao: 'Início, tarefas e cadastros' },
  { chave: 'labmanager', nome: 'LabManager', descricao: 'Laboratório, Campo e Controle tecnológico' },
  { chave: 'campo', nome: 'Campo', descricao: 'Sondagem e coleta' },
  { chave: 'comercial', nome: 'Comercial', descricao: 'Atendimento, oportunidades e funil' },
];

/** O nome de um espaço como a faixa o escreve. */
export function nomeDoEspaco(e: EspacoDeTrabalho): string {
  return ESPACOS.find((x) => x.chave === e)?.nome ?? e;
}

/** A linha de descrição de um espaço, como o seletor a escreve sob o nome. */
export function descricaoDoEspaco(e: EspacoDeTrabalho): string {
  return ESPACOS.find((x) => x.chave === e)?.descricao ?? '';
}

/** Onde o módulo mora, com o padrão aplicado. */
export const localDe = (m: Modulo): Local => m.local ?? 'menu';

/**
 * Os módulos de menu de um espaço, na ordem do registro.
 *
 * Só o que `local` manda para o menu (`engrenagem` e `oculto` ficam de
 * fora) e que mora no espaço. É a base das três perguntas abaixo — o menu
 * do espaço, os espaços que a pessoa alcança e a rota do espaço —, para que
 * as três concordem sobre o que «o menu do espaço» contém.
 */
function menuDoEspaco(espaco: EspacoDeTrabalho): Modulo[] {
  return MODULOS.filter((m) => localDe(m) === 'menu' && m.espacos.includes(espaco));
}

/**
 * Os módulos do menu de um espaço, agrupados como o painel lateral desenha.
 *
 * Agrupa por nome de grupo e não por corrida consecutiva — que era como
 * fazia até 23/08/2026. A diferença aparece agora que os métodos ficam
 * `oculto` no meio do bloco Bancada: com o agrupamento por corrida, tirar
 * um item do meio partia o grupo em dois cabeçalhos "Bancada" iguais,
 * um embaixo do outro.
 *
 * ── O espaço (onda 293) ──────────────────────────────────────────────
 *
 * Cada espaço mostra só o que mora nele (`Modulo.espacos`); um grupo que
 * ficaria vazio no espaço nem entra na lista — o Hub não tem «Bancada».
 *
 * A ORDEM dos grupos, porém, é a do registro INTEIRO (hoje: sem título,
 * Trabalho, Bancada, Saída, SGQ), e não a da primeira aparição dentro do
 * espaço. A diferença é do Campo: `equipamentos` (SGQ) vem antes de
 * `campo` (Trabalho) no registro, e na ordem do espaço o menu do Campo
 * sairia «SGQ, Trabalho» — o grupo de qualidade em cima do de trabalho,
 * ao contrário dos outros dois espaços. A ordem sai de `MODULOS`, e não
 * de uma segunda lista de nomes de grupo escrita aqui.
 */
export function menuPorGrupo(espaco: EspacoDeTrabalho): { grupo: string | null; itens: Modulo[] }[] {
  const ordem: (string | null)[] = [];
  for (const m of MODULOS) {
    if (localDe(m) === 'menu' && !ordem.includes(m.grupo)) ordem.push(m.grupo);
  }
  const doEspaco = menuDoEspaco(espaco);
  return ordem
    .map((grupo) => ({ grupo, itens: doEspaco.filter((m) => m.grupo === grupo) }))
    .filter((g) => g.itens.length > 0);
}

/** O que a engrenagem, ao lado do nome de quem está logado, oferece. */
export function itensDaEngrenagem(): Modulo[] {
  return MODULOS.filter((m) => localDe(m) === 'engrenagem');
}

/**
 * Os módulos que uma porta abre — os cartões da tela de Cadastros.
 *
 * Na ordem do registro, como o menu. A porta não tem lista própria
 * escrita à mão: se um cadastro novo declarar `dentroDe`, ele aparece
 * aqui sem que ninguém precise lembrar de acrescentá-lo.
 */
export function modulosDentroDe(chave: string): Modulo[] {
  return MODULOS.filter((m) => m.dentroDe === chave);
}

/** O módulo pela chave, para quem precisa de um item fora do menu. */
export function moduloPorChave(chave: string): Modulo | undefined {
  return MODULOS.find((m) => m.chave === chave);
}

/**
 * O módulo que responde por um endereço — o mesmo que o menu acende.
 *
 * Empata pelo prefixo mais longo: `/equipamentos/verificacoes` é de
 * Equipamentos, e `/os/…/programar` é do módulo `ensaios` (raiz
 * `/programar` não casa; a rota `/os/:id/programar` casa pelo sufixo). Esse
 * endereço, porém, só existe como redirecionamento, fora da moldura: a aba
 * Programação que a pessoa vê é `/os/:id/programacao`, que é do módulo `os`
 * e, na H0, SEGUE o espaço da OS (o escolhido). A decisão de 06/10 («a
 * programação dos ensaios fica no LabManager») vale para a OS integrada da
 * H2, quando a `-L` nasce e a aba muda de espaço com ela.
 *
 * Morava em `Caminho.tsx` (onda 188, a trilha) e veio para cá na onda 293
 * (os espaços na navegação): a pergunta «em que espaço está esta tela?»
 * (`espacoDaTela`) parte do mesmo módulo do endereço, e o registro não
 * deve depender de um componente de tela para responder.
 */
export function moduloDoEndereco(onde: string): Modulo | undefined {
  let melhor: { m: Modulo; peso: number } | undefined;
  for (const m of MODULOS) {
    if (!m.rota || m.situacao !== 'no_ar') continue;
    let peso = -1;
    if (m.rota === '/') {
      if (onde === '/' || (m.raiz && onde.startsWith(m.raiz))) peso = 1;
    } else if (m.rota.includes('/:')) {
      // `/ensaio/:id/adensamento` casa `/ensaio/<uuid>/adensamento`.
      const padrao = new RegExp(`^${m.rota.replace(/:[a-z]+/gi, '[^/]+')}(?:/|$)`);
      if (padrao.test(onde)) peso = m.rota.length;
    } else if (onde === m.rota || onde.startsWith(`${m.rota}/`)) {
      peso = m.rota.length;
    } else if (m.raiz && (onde === m.raiz || onde.startsWith(`${m.raiz}/`))) {
      peso = m.raiz.length;
    }
    if (peso >= 0 && (!melhor || peso > melhor.peso)) melhor = { m, peso };
  }
  return melhor?.m;
}

/**
 * Se a sessão ALCANÇA um módulo (UX-015/UX-068).
 *
 * Um módulo declara os domínios de permissão que o governam
 * (`permissoes`). Se a sessão não tem NENHUMA permissão em nenhum
 * deles, o item some — para o auxiliar não abrir «Painel» e receber uma
 * recusa, nem «Medições» e outra. O critério é por domínio, e não por
 * `.ver`, porque há domínios sem `.ver` (recebimento) e há quem só
 * tenha o verbo (`recebimento.atribuir_os`) e precise da tela. Módulo
 * sem `permissoes` declaradas continua aparecendo — hoje só o que não é
 * item de menu (o que se alcança por dentro de outra tela): a matriz manda lá
 * dentro, como sempre mandou.
 *
 * Mora aqui, e não na Moldura, desde que a tela de Cadastros passou a
 * desenhar cartões pela mesma regra: duas cópias da regra seriam duas
 * chances de o menu e a porta discordarem sobre o que a pessoa alcança.
 */
export function alcanca(m: Modulo, permissoes: readonly string[]): boolean {
  if (!m.permissoes || m.permissoes.length === 0) return true;
  return m.permissoes.some((dom) => permissoes.some((p) => p.startsWith(`${dom}.`)));
}

// ── Os espaços de trabalho: o que a pessoa alcança e onde ela está ──────

/**
 * Um módulo que serve de destino para o clique num espaço: no ar, com rota
 * fixa (sem `/:` — uma ficha não abre sem o id) e que a pessoa alcança.
 *
 * As duas perguntas que dependem disso — «a pessoa alcança este espaço?» e
 * «para onde leva o clique nele?» — usam este mesmo critério, para que um
 * espaço nunca conte como alcançado sem ter para onde levar.
 */
function destinoAlcancado(m: Modulo, permissoes: readonly string[]): boolean {
  return m.situacao === 'no_ar' && !!m.rota && !m.rota.includes('/:') && alcanca(m, permissoes);
}

/**
 * Os espaços de trabalho da sessão, na ordem da faixa.
 *
 * `codigos` é o `Eu.espacos` que o `/auth/eu` entrega: os espaços que a
 * pessoa alcança, calculados no banco (`vw_usuario_espaco`) com o `pode()`
 * de sempre — a web deixou de deduzi-los pelo prefixo das permissões
 * (H1, spec 03 §9). Só valem os que a faixa conhece (`ESPACOS`), e na ordem
 * dela, e não na ordem em que a lista veio; um código que o registro não
 * conhece (um espaço novo no banco, ainda sem tela) fica de fora.
 */
export function espacosDaSessao(codigos: readonly string[]): EspacoDeTrabalho[] {
  return ESPACOS.filter((e) => codigos.includes(e.chave)).map((e) => e.chave);
}

/**
 * A preferência de cada espaço, módulo a módulo: o primeiro que a pessoa
 * alcança é para onde o clique no nome do espaço leva. Mora no código,
 * junto com `ESPACOS`: é uma escolha de tela, e não de acesso.
 */
const PREFERENCIA_DO_ESPACO: Record<EspacoDeTrabalho, string[]> = {
  /* 550 (Tarefa 20): o Hub abre no Início, «O que precisa de você hoje». */
  hub: ['inicio', 'os', 'mapa'],
  labmanager: ['painel', 'fila', 'amostras', 'os'],
  campo: ['campo'],
  /* 552 (Tarefa 28): quem não atende (só o CRM) cai no painel; depois, no funil. */
  comercial: ['caixa', 'painel_comercial', 'funil'],
};

/**
 * Para onde leva o clique no nome do espaço: o primeiro módulo da lista de
 * preferência que a pessoa alcança.
 *
 * A lista do LabManager é a ordem da `Porta` de hoje (UX-015: Painel, Fila,
 * Amostras, OS) — quem tem o painel cai nele, quem só tem a bancada cai na
 * fila. Se nenhum da lista é alcançado, vale o primeiro módulo alcançado do
 * menu do espaço, na ordem do registro; se nem esse, `null` — as permissões
 * não dão destino nenhum neste espaço (`portaDaSessao` passa ao seguinte, e
 * o teste `282.31` cobra que o «Ver» de toda unidade dê um destino).
 *
 * Só valem módulos de menu do próprio espaço, no ar e com rota fixa. A rota
 * `'/'` do Painel sai como `'/painel'`, o endereço que o menu usa.
 */
export function rotaDoEspaco(
  espaco: EspacoDeTrabalho,
  permissoes: readonly string[],
): string | null {
  const alcancados = menuDoEspaco(espaco).filter((m) => destinoAlcancado(m, permissoes));
  const escolhido = PREFERENCIA_DO_ESPACO[espaco]
    .map((chave) => alcancados.find((m) => m.chave === chave))
    .find((m) => m !== undefined) ?? alcancados[0];
  if (!escolhido?.rota) return null;
  return escolhido.rota === '/' ? '/painel' : escolhido.rota;
}

/**
 * Os espaços que a faixa (e o topo da gaveta) oferece: os alcançados que têm
 * para onde levar, na ordem em que vieram (a da faixa, `espacosDaSessao`).
 *
 * Alcançar é ter qualquer permissão de uma unidade do espaço (RN-242), e
 * algumas não casam com módulo de menu nenhum do espaço delas —
 * `relatorio.disponibilizar` (Portal), `acervo.importar` e `acervo.baixar`
 * (Mapa), `dado.exportar` (Painel). Uma exceção individual antiga com só uma delas
 * faz o espaço ser alcançado sem destino, e o link dele levaria a `/`. A
 * porta já pula esse espaço (`portaDaSessao`); a faixa também. O 282.34
 * confere, permissão a permissão, que ou o espaço tem destino ou não entra.
 */
export function espacosDaFaixa(
  espacos: readonly EspacoDeTrabalho[],
  permissoes: readonly string[],
): EspacoDeTrabalho[] {
  return espacos.filter((e) => rotaDoEspaco(e, permissoes) !== null);
}

/**
 * Em que espaço está a tela do endereço aberto — RN-240 (onda 293, spec 03
 * §3.2): o espaço da tela segue o endereço; módulo compartilhado mantém o
 * espaço escolhido; tela da engrenagem e endereço desconhecido mantêm o
 * escolhido, se alcançado.
 *
 * `escolhido` é o espaço que a pessoa clicou na faixa (ou `null`, se ainda
 * não clicou em nenhum); `alcancados`, o que ela alcança (`espacosDaSessao`).
 * Nesta ordem:
 *
 *  1. Os `candidatos` são os espaços de trabalho do módulo do endereço (o
 *     `'sistema'` não conta — não é espaço da faixa).
 *  2. Sem candidatos — endereço que nenhum módulo reclama, ou tela da
 *     engrenagem —, a faixa não muda: vale o `escolhido`, mas só se a
 *     pessoa o alcança (depois de «Entrar como» outra pessoa, o escolhido
 *     de antes pode já não valer). Senão o primeiro que ela alcança, e, se
 *     nenhum, o Hub.
 *  3. O `escolhido` está entre os candidatos E a pessoa o alcança: fica
 *     nele. É o módulo compartilhado — `/os` abre no Hub ou no LabManager
 *     conforme a escolha. Um escolhido que ela já não alcança (depois de
 *     «Entrar como» outra pessoa, ou depois de a exceção individual tirar
 *     o espaço) não vale nem aqui: cai na regra 4. É o «nunca um espaço que
 *     a pessoa não alcança» valendo também para o módulo compartilhado.
 *  4. Senão o endereço manda: o primeiro candidato que a pessoa alcança, ou
 *     o primeiro candidato se nenhum — é o espaço que a tela PEDE, e a
 *     permissão de verdade é a da API. Quem decide o que fazer quando a
 *     pessoa não o alcança é `recusaDaTela`.
 */
export function espacoDaTela(
  onde: string,
  escolhido: EspacoDeTrabalho | null,
  alcancados: readonly EspacoDeTrabalho[],
): EspacoDeTrabalho {
  const candidatos = (moduloDoEndereco(onde)?.espacos ?? [])
    .filter((e): e is EspacoDeTrabalho => e !== 'sistema');
  if (candidatos.length === 0) {
    if (escolhido && alcancados.includes(escolhido)) return escolhido;
    return alcancados[0] ?? 'hub';
  }
  if (escolhido && candidatos.includes(escolhido) && alcancados.includes(escolhido)) return escolhido;
  return candidatos.find((e) => alcancados.includes(e)) ?? candidatos[0];
}

/**
 * Para onde a sessão entra: o espaço e a porta dele.
 *
 * Quem tem mais de um espaço entra pelo Hub e, se as permissões não lhe dão
 * destino nele, pelos outros, na ordem da faixa; quem tem um só, por ele. É
 * o primeiro espaço com `rotaDoEspaco` não nulo — assim a porta nunca leva a
 * um espaço onde a pessoa chega e não encontra nada. `null` quando nenhum
 * dá destino: a conta alcança espaços que o registro não sabe abrir, ou não
 * alcança nenhum, e a tela mostra «Sua conta ainda não tem acesso».
 *
 * `espacos` vem de `espacosDaSessao` (na ordem da faixa, que põe o Hub à
 * frente); o Hub é tentado primeiro mesmo que a lista chegue fora de ordem.
 */
export function portaDaSessao(
  espacos: readonly EspacoDeTrabalho[],
  permissoes: readonly string[],
): { espaco: EspacoDeTrabalho; rota: string } | null {
  const ordem = espacos.length > 1
    ? [...espacos.filter((e) => e === 'hub'), ...espacos.filter((e) => e !== 'hub')]
    : [...espacos];
  for (const espaco of ordem) {
    const rota = rotaDoEspaco(espaco, permissoes);
    if (rota !== null) return { espaco, rota };
  }
  return null;
}

/**
 * Por que a tela não abre: a conta não alcança espaço nenhum (`conta`) ou
 * alcança, mas não o da tela (`espaco`, com o espaço de volta). Ver
 * `recusaDaTela`.
 */
export type Recusa =
  | { tipo: 'conta' }
  | { tipo: 'espaco'; espaco: EspacoDeTrabalho; voltar: EspacoDeTrabalho };

/**
 * O que a tela do endereço mostra no lugar dela quando a pessoa não pode
 * estar ali (spec 03 §3.3, §9): a conta sem espaço nenhum, ou o espaço da
 * tela que ela não alcança.
 *
 * Nesta ordem:
 *
 *  1. `/` é a porta: ela decide para onde ir (`portaDaSessao`), e a conta
 *     sem espaço é tratada lá — `null`.
 *  2. Endereço sem espaço de trabalho no módulo — a engrenagem (Relatos,
 *     Usuários…) ou um endereço que nenhum módulo reclama — não é tela de
 *     espaço: `null`. A conta sem espaço, mas com a engrenagem, ainda abre
 *     Relatos (ponto de atenção 4), e quem digita um endereço desconhecido
 *     cai na página de «não encontrado», e não numa recusa.
 *  3. Sem espaço algum, `{ tipo: 'conta' }`: nunca um «sem acesso ao Hub»
 *     com «Voltar» para lugar nenhum.
 *  4. O espaço que a tela pede (`espacoDaTela`, RN-240) é alcançado: `null`.
 *  5. Senão `{ tipo: 'espaco', espaco, voltar }`: o espaço recusado e aquele
 *     para onde o botão leva — o escolhido, se a pessoa o alcança; senão o
 *     primeiro que ela alcança.
 *
 * A recusa da tela é só a explicação: o que vale é a API, que recusa com a
 * mesma frase (`fn_recusa_de_espaco`, RN-242).
 */
export function recusaDaTela(
  onde: string,
  escolhido: EspacoDeTrabalho | null,
  alcancados: readonly EspacoDeTrabalho[],
): Recusa | null {
  if (onde === '/') return null;
  const daTela = (moduloDoEndereco(onde)?.espacos ?? []).filter((e) => e !== 'sistema');
  if (daTela.length === 0) return null;
  if (alcancados.length === 0) return { tipo: 'conta' };
  const espaco = espacoDaTela(onde, escolhido, alcancados);
  if (alcancados.includes(espaco)) return null;
  return {
    tipo: 'espaco',
    espaco,
    voltar: escolhido && alcancados.includes(escolhido) ? escolhido : alcancados[0],
  };
}
